<script setup lang="ts">
import { ref } from "vue";
import MarkdownIt from "markdown-it";
import { computed } from "vue";
import SharedCompliantForm from "~/components/Shared/CompliantForm.vue";
import ConfirmationModal from "@/components/Shared/ConfirmationModal.vue";
import { useRoute } from "vue-router";

const props = defineProps<{
  content: {
    title: string;
    content: string;
    iframeUrl: string;
    slug?: string;
  };
}>();

const showModal = ref(false);
const md = new MarkdownIt({
  html: true, // Enable HTML tags for phone number links
  linkify: true, // Enable auto-converting URL-like text to links
  breaks: true, // Convert \n to <br>
});

// Add custom renderer for phone links
md.renderer.rules.link_open = (
  tokens: any,
  idx: any,
  options: any,
  env: any,
  self: any
) => {
  const token = tokens[idx];
  const href = token.attrGet("href");

  if (href && href.startsWith("tel:")) {
    // Format phone number by removing spaces
    const phone = href.replace("tel:", "").replace(/\s+/g, "");
    token.attrSet("href", `tel:${phone}`);
  }

  return self.renderToken(tokens, idx, options);
};

// Check if content contains ###FORM###
const hasForm = computed(() => {
  return props.content.content.includes("###FORM###");
});

// Content before form
const contentBeforeForm = computed(() => {
  if (!hasForm.value) return "";

  const parts = props.content.content.split("###FORM###");
  let cleanContent = parts[0]
    .split("\n")
    .map((line) => {
      return line.replace(/^(\s*)•\s/, "$1- ");
    })
    .join("\n");

  cleanContent = cleanContent.replace(
    /\[(\d[\d\s]+)\]\(tel:[\d\s]+\)/g,
    (match, phone) => {
      const cleanPhone = phone.replace(/\s+/g, "");
      return `[${phone}](tel:${cleanPhone})`;
    }
  );

  return md.render(cleanContent);
});

// Content after form
const contentAfterForm = computed(() => {
  if (!hasForm.value) return "";

  const parts = props.content.content.split("###FORM###");
  if (parts.length < 2) return "";

  let cleanContent = parts[1]
    .split("\n")
    .map((line) => {
      return line.replace(/^(\s*)•\s/, "$1- ");
    })
    .join("\n");

  cleanContent = cleanContent.replace(
    /\[(\d[\d\s]+)\]\(tel:[\d\s]+\)/g,
    (match, phone) => {
      const cleanPhone = phone.replace(/\s+/g, "");
      return `[${phone}](tel:${cleanPhone})`;
    }
  );

  return md.render(cleanContent);
});

// Convert markdown content to HTML
const renderedContent = computed(() => {
  let cleanContent = props.content.content
    .split("\n")
    .map((line) => {
      // Convert bullet points to dashes while preserving indentation
      return line.replace(/^(\s*)•\s/, "$1- ");
    })
    .join("\n");

  // Find and format phone number links
  // Example: Call [02 5126 0000](tel:02 5126 0000)
  cleanContent = cleanContent.replace(
    /\[(\d[\d\s]+)\]\(tel:[\d\s]+\)/g,
    (match, phone) => {
      const cleanPhone = phone.replace(/\s+/g, "");
      return `[${phone}](tel:${cleanPhone})`;
    }
  );

  return md.render(cleanContent);
});

// Get current route to check if we're on financial hardship page
const route = useRoute();
const isFinancialHardshipPage = computed(() => {
  return route.path.includes("legals-financial-hardship");
});

// Scroll to top when component is mounted
// onMounted(() => {
//   console.log("onMounted");
//   window.scrollTo({
//     top: 0,
//     behavior: "smooth",
//   });
// });
</script>
<template>
  <section class="content-block">
    <div class="max-w-[1430px] m-auto px-4 py-8">
      <div class="space-y-8">
        <!-- Title and Content Section -->
        <div class="prose max-w-none">
          <h1 class="text-primary text-[36px] font-[900] mb-6">
            {{ content.title }}
          </h1>
          <!-- If content contains ###FORM###, replace it with SharedCompliantForm component -->
          <div v-if="hasForm" class="text-font-primary">
            <div v-html="contentBeforeForm" class="content-html"></div>
            <SharedCompliantForm
              :hideHardshipLine="isFinancialHardshipPage"
              @submitted="showModal = true"
            />
            <ConfirmationModal v-if="showModal" @close="showModal = false" />
            <div v-html="contentAfterForm" class="content-html"></div>
          </div>
          <!-- If content does not contain ###FORM###, show normal content -->
          <div
            v-else
            v-html="renderedContent"
            class="text-font-primary content-html"
          ></div>
        </div>
      </div>
    </div>
  </section>
</template>

<style>
/* Global styles for rendered HTML content */
.content-html table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #e5e7eb;
  margin-bottom: 1rem;
}

.content-html th {
  background-color: #f9fafb;
  padding: 0.75rem;
  text-align: left;
  font-weight: 600;
  border: 1px solid #e5e7eb;
}

.content-html td {
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
}

.content-html tr {
  border: 1px solid #808080;
}

.content-html tr:nth-child(odd) td {
  background-color: #f3f4f6;
}

.content-html tr:nth-child(even) td {
  background-color: #ffffff;
}

ol {
  list-style: auto;
}

ol > li {
  font-size: 24px;
  margin-left: 2rem !important;
}

ol > li > p:first-child {
  font-size: 24px;
}
</style>

<style scoped>
.content-block {
  @apply bg-white;
}

.prose {
  h2,
  h3,
  h4,
  h5,
  h6 {
    @apply text-primary;
  }
}
</style>
